//@flow
import { FormControl, FormLabel } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import { DataGridPremium, GridToolbar } from "@mui/x-data-grid-premium";
import axios from "axios";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import PageContentWrapper from "../../components/mui/app/pageContentWrapper/pageContentWrapper";
import PageTitle from "../../components/mui/app/pageTitle/pageTitle";
import CustomAutocomplete from "../../components/mui/form/autocomplete/autocomplete";
import DateRange from "../../components/mui/form/dateRange/dateRange";
import CollapseBox from "../../components/mui/general/collapseBox/collapseBox";
import SkeletonLoaderWrapper from "../../components/mui/general/skeletonLoaderWrapper/skeletonLoaderWrapper";
import { CustomPagination } from "../../components/mui/grids/dataGridCustom/customPagination";
import CustomToolbar from "../../components/mui/grids/dataGridCustom/customToolbar/customToolbar";
import DataGridWrapper from "../../components/mui/grids/dataGridWrapper/dataGridWrapper";
import { API } from "../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../helpers/errorHandling";
import { mapDateRange } from "../../helpers/helpers";
import { muiGridLocales } from "../../helpers/mui/muiLocales";
import { statistics_view_customer, statistics_view_mtbf } from "../../helpers/rights/rights";
import { formControlDefaults, numericFormatDigitDefaults } from "../../helpers/theme/defaultProps";
import useReportStaticData from "../../hooks/reports/useReportStaticData";
import useUserAction from "../../hooks/user/useUserAction";
import useUserData from "../../hooks/user/useUserData";
import { REPORTS } from "../../locales/pages/namespaces";
import PermissionHOC, { withPermissionHOC } from "../../modules/permission/components/permissionHOC/permissionHOC";
import TruncateDescription from "../dashboard/partials/truncateDescription";

export const mapFilters = (filtersData) => {
	const {
		Items,
		SubmitDt,
		Address,
		Asset,
		Customer,
		Kind
	} = filtersData

	const [SubmitDtFrom, SubmitDtTo] = mapDateRange(SubmitDt)

	const getIds = (arr) =>
		Array.isArray(arr) && arr.length > 0
			? arr.map(i => i.id).join(',')
			: ''

	const filtersObject = {
		Items: getIds(Items),
		Address: getIds(Address),
		SubmitDtFrom: SubmitDtFrom || '',
		SubmitDtTo: SubmitDtTo || '',
		Asset: getIds(Asset),
		Customer: getIds(Customer),
		Kind: getIds(Kind)
	}

	return { fields: filtersObject }
}
type Props = {};
const Mtbf = (props: Props) => {
	const { t } = useTranslation(REPORTS)
	const { dateFormat, token, isLoggedIn, language, thousandSeparator, decimalSeparator } = useUserData()
	const { userLogout } = useUserAction()
	const [loadingSearch, setLoadingSearch] = useState(false)
	const [rows, setRows] = useState([])
	const [dynamicColumns, setDynamicColumns] = useState([])
	const [filtersData, setFiltersData] = useState({
		Items: [],
		SubmitDt: [],
		Address: [],
		Asset: [],
		Customer: [],
		Kind: []
	})
	const {
		loading,
		items,
		addresses,
		assets,
		customers,
		kinds
	} = useReportStaticData()
	const [customerOptions, setCustomerOptions] = useState(customers)
	const [sitesData, setSitesData] = useState(addresses)
	const [customerAddresses,setCustomerAddresses]= useState([])
	const [assetsData, setAssetsData] = useState(assets)
	const [itemData, setItemData] = useState(items)
	const [addressInput, setAddressInput] = useState('');

	useEffect(() => {
		setCustomerOptions(customers || []);
		setSitesData(addresses || [])
		setAssetsData(assets || [])
		setItemData(items || [])
	}, [customers, addresses, assets, items]);

	const handleSearch = (params, isClear = false) => {
		const { SubmitDtFrom, SubmitDtTo } = params.fields || {};
		if (!isClear && (!SubmitDtFrom || !SubmitDtTo)) {
			enqueueSnackbar(t('Η ημερομηνία είναι υποχρεωτική!'), {
				variant: 'warning',
				autoHideDuration: 3000,
			});
			return;
		}
		setLoadingSearch(true)
		axios({
			method: 'post',
			url: `${API}/en/custom/action`,
			data: {
				action_id: 'mtbf',
				fields: params.fields
			},
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
		}).then(res => {
			console.log('res', res.data)
			const data = res.data.data || []
			setRows(data)
			setLoadingSearch(false)

			{
				data.length === 0 && !isClear &&
				enqueueSnackbar(t('No data found!'), {
					variant: 'warning',
					autoHideDuration: 3000,
				})
			}
		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}

	const columns = useMemo(() => [
		{
			field: 'Item',
			headerName: t('Services'),
			description: t('Services'),
			flex: 1,
			minWidth: 150,
			renderCell: ({ row }) => {
				return <TruncateDescription value={row.Item || '-'}/>
			},
		},
		{
			field: 'Address',
			headerName: t('Site'),
			description: t('Site'),
			flex: 1,
			minWidth: 150,
			renderCell: ({ row }) => {
				return <TruncateDescription value={row.Address || '-'}/>
			},
		},
		{
			field: 'Asset',
			headerName: t('Asset'),
			description: t('Asset'),
			flex: 1,
			minWidth: 150,
			renderCell: ({ row }) => {
				return <TruncateDescription value={row.Asset || '-'}/>
			},
		},
		{
			field: 'MonthMTBF',
			headerName: t('Range MTBF(days)'),
			description: t('Range MTBF(days)'),
			flex: 1,
			minWidth: 150,
			type: 'number',
			valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
			renderCell: (params) => {
				const { value } = params
				return (
					<NumericFormat
						{...numericFormatDigitDefaults}
						thousandSeparator={thousandSeparator}
						decimalSeparator={decimalSeparator}
						displayType="text"
						value={value}
					/>
				)
			}
		},
		{
			field: 'HitMTBF',
			headerName: t('Heat MTBF(days)'),
			description: t('Heat MTBF(days)'),
			flex: 1,
			minWidth: 150,
			type: 'number',
			valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
			renderCell: (params) => {
				const { value } = params
				return (
					<NumericFormat
						{...numericFormatDigitDefaults}
						thousandSeparator={thousandSeparator}
						decimalSeparator={decimalSeparator}
						displayType="text"
						value={value}
					/>
				)
			}
		},

	], [t, decimalSeparator])

	const searchCustomers = useCallback((evt) => {
		setLoadingSearch(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			data: {
				query: evt.target.value,
			},
			url: `${API}/en/customers/search`
		}).then((response) => {
			setLoadingSearch(false)
			setCustomerOptions(response.data.data)
		}).catch((error) => {
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			setLoadingSearch(false)
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout])
	const searchSites = useCallback((query='') => {
		const getIds = (arr) =>
			Array.isArray(arr) && arr.length > 0
				? arr.map(i => i.id).join(',')
				: ''
		const customerIds = getIds(filtersData.Customer)
		setLoadingSearch(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			data: {
				CustId:customerIds,
				query:query ,
				...(customerIds && { CustId: customerIds }),
				Type: 'Site'
			},
			url: `${API}/en/customer/address/search`
		}).then((response) => {
			setSitesData(response.data.data)
			setLoadingSearch(false)
		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [filtersData.Customer, t, isLoggedIn, userLogout])

	const onChangeCustomer = useCallback((newValue) => {
		const getIds = (arr) =>
			Array.isArray(arr) && arr.length > 0
				? arr.map(i => i.id).join(',')
				: ''
		const customerIds = getIds(newValue)

		setLoadingSearch(true)
		axios({
			method: 'post',
			data: {
				CustId: customerIds,
				ReturnData: ['addresses', 'assignees', 'FM']
			},
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			url: `${API}/en/issues/issueCustData`
		}).then((res) => {
			setLoadingSearch(false)
			setSitesData(_.orderBy(res.data?.addresses?.map(i => ({
				id: i.id,
				Description: i.Site
			})), ['Description'], ['asc']))
			setCustomerAddresses(_.orderBy(res.data?.addresses?.map(i => ({
				id: i.id,
				Description: i.Site
			})), ['Description'], ['asc']))

		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout])
	const onChangeItem = useCallback((newValue) => {
		const getIds = (arr) =>
			Array.isArray(arr) && arr.length > 0
				? arr.map(i => i.id).join(',')
				: ''
		const itemIds = getIds(newValue)
		const addressIds = getIds(filtersData.Address)
		setLoadingSearch(true)
		axios({
			method: 'post',
			data: {
				ItemId: itemIds,
				AddressId: addressIds,
				ReturnData: ['assets']
			},
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			url: `${API}/en/issues/issueCustData`
		}).then((res) => {
			setLoadingSearch(false)
			setAssetsData(_.orderBy(res.data?.assets?.map(i => ({
				id: i.id,
				Description: i.Description
			})), ['Description'], ['asc']))

		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout, filtersData.Address])

	return (
		<Box>
			<PageContentWrapper>
				<PageTitle title={t('MTBF')}/>
				<SkeletonLoaderWrapper loading={loading}>
					<CollapseBox title={t('Filter Options')}>
						<Grid container spacing={2} sx={{ mt: 1, alignItems: 'flex-end' }}>
							<PermissionHOC requiredPermissions={[statistics_view_customer]}>
								<Grid size={{ xs: 6, sm: 4, md: 4, lg: 4 }}>
									<FormControl
										{...formControlDefaults}
									>
										<FormLabel>{t('Customer')}</FormLabel>
										<CustomAutocomplete
											identifier="id"
											name="Customer"
											onChange={(evt, newValue, reason) => {
												setFiltersData({
													...filtersData,
													Customer: newValue
												})
												if (reason === 'clear') {
													setCustomerOptions([...customers])
													setSitesData([...addresses])
												} else {
													onChangeCustomer(newValue)
												}
											}}
											value={filtersData.Customer}
											options={customerOptions}
											labelKey={'Description'}  //LoginName or AssigneeName
											placeholder={t('Select one or more..')}
											onInputChange={(evt) => searchCustomers(evt)}
											otherprops={{
												multiple: true,
												disableCloseOnSelect: true,
												limitTags: 1,
												getOptionKey: (option) => (option?.id)
											}}
										/>
									</FormControl>
								</Grid>
							</PermissionHOC>
							<Grid size={{ xs: 6, sm: 4, md: 4, lg: 4 }}>
								<FormControl
									{...formControlDefaults}
								>
									<FormLabel>{t('Site')}</FormLabel>
									<CustomAutocomplete
										identifier="id"
										name="Address"
										onChange={(evt, newValue, reason) => {
											setFiltersData({
												...filtersData,
												Address: newValue
											})
											if (reason === 'clear' && !filtersData.Customer?.id) {
												setSitesData([...addresses])
												setAddressInput('');
											}
										}}
										value={filtersData.Address}
										options={sitesData}
										labelKey={'Description'}  //LoginName or AssigneeName
										placeholder={t('Select one or more..')}
										otherprops={{
											multiple: true,
											disableCloseOnSelect: true,
											limitTags: 1,
											getOptionKey: (option) => (option?.id),
											inputValue:addressInput,
											onInputChange: (event, newValue) => {
												setAddressInput(newValue ?? '');
												searchSites(newValue);
											},
											onClose: (event, reason) => {
												if (['blur', 'escape', 'toggleInput'].includes(reason)) {
													setSitesData(customerAddresses || []);
													setAddressInput('');
												}
											},
										}}
									/>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
								<FormControl {...formControlDefaults}>
									<FormLabel>{t('Submit Date')}</FormLabel>
									<DateRange
										dateFormat={dateFormat}
										value={filtersData.SubmitDt}
										onChange={(newValue) => {
											setFiltersData(prev => ({
												...prev,
												SubmitDt: newValue
											}))
										}}
										onClear={() => (
											setFiltersData(prev => ({
												...prev,
												SubmitDt: null
											}))
										)}
									/>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
								<FormControl
									{...formControlDefaults}
								>
									<FormLabel>{t('Services')}</FormLabel>
									<CustomAutocomplete
										identifier="id"
										name="Items"
										onChange={(evt, newValue, reason) => {
											setFiltersData({
												...filtersData,
												Items: newValue
											})
											if (reason === 'clear') {
												setItemData([...items])
												setAssetsData([...assets])
											} else {
												onChangeItem(newValue)
											}
										}}
										value={filtersData.Items}
										options={items}
										labelKey={'Description'}  //LoginName or AssigneeName
										placeholder={t('Select one or more..')}
										otherprops={{
											multiple: true,
											disableCloseOnSelect: true,
											limitTags: 1,
											getOptionKey: (option) => (option?.id)
										}}
									/>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
								<FormControl
									{...formControlDefaults}
								>
									<FormLabel>{t('Asset')}</FormLabel>
									<CustomAutocomplete
										identifier="id"
										name="Asset"
										onChange={(evt, newValue, reason) => {
											setFiltersData({
												...filtersData,
												Asset: newValue
											})
											if (reason === 'clear' && !filtersData.Asset?.id) {
												setAssetsData([...assets])
											}
										}}
										value={filtersData.Asset}
										options={assetsData}
										labelKey={'Description'}
										placeholder={t('Select one or more..')}
										otherprops={{
											multiple: true,
											disableCloseOnSelect: true,
											limitTags: 1,
											getOptionKey: (option) => (option?.id)
										}}
									/>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
								<FormControl
									{...formControlDefaults}
								>
									<FormLabel>{t('Kind')}</FormLabel>
									<CustomAutocomplete
										identifier="id"
										name="Kind"
										onChange={(evt, newValue, reason) => {
											setFiltersData({
												...filtersData,
												Kind: newValue
											})
										}}
										value={filtersData.Kind}
										options={kinds ?? []}
										labelKey={'Description'}
										placeholder={t('Select one or more..')}
										otherprops={{
											multiple: true,
											disableCloseOnSelect: true,
											limitTags: 1,
											getOptionKey: (option) => (option?.id)
										}}
									/>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 6, sm: 6, md: 4, lg: 3 }} sx={{ p: 0.4 }}>
								<Box display={'flex'} alignItems={'flex-end'} justifyContent={'center'}
									 flexWrap={'wrap'} gap={1}>
									<Button
										loading={loadingSearch}
										onClick={() => {
											const params = {
												...mapFilters(filtersData)
											}
											handleSearch(params, false)
										}}
										variant="outlined"
										color={'success'}
									>
										{t('Search')}
									</Button>
									<Button
										loading={loadingSearch}
										onClick={() => {
											const clearedFilters = {
												SubmitDt: null,
												Address: [],
												Items: [],
												Asset: [],
												Customer: [],
												Kind: []
											}
											setFiltersData(clearedFilters)

											const params = mapFilters(clearedFilters)
											handleSearch(params, true)
										}}
										variant="outlined"
										color={'error'}
									>
										{t('Clear')}
									</Button>
								</Box>
							</Grid>
						</Grid>
					</CollapseBox>
				</SkeletonLoaderWrapper>
				{rows.length > 0 &&
					<DataGridWrapper sx={{ height: 700}}>
						<DataGridPremium
							rows={rows}
							columns={columns}
							loading={loadingSearch}
                            getRowId={(row)=>row.id}
							pagination
							pageSize={100}
							rowsPerPageOptions={[10, 20, 50, 100]}
							initialState={{
								density: 'compact'
							}}
							slots={{
								toolbar: GridToolbar,
								pagination: CustomPagination,
							}}
							disableSelectionOnClick
							localeText={muiGridLocales(language)}
						/>

					</DataGridWrapper>}
			</PageContentWrapper>
		</Box>
	);
}
export default withPermissionHOC(Mtbf, [statistics_view_mtbf])